<template>
  <template v-if="$route.name === 'exams.management'">
    <div class="col-12 col-xl-12 ">
      <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
              {{ $t('examsManagement.title') }}
            </h6>
          </div>
        </div>

        <div class="card-body px-0 pb-2 pe-xl-3">
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">{{ $t('examsManagement.loading') }}</p>
          </div>

          <div v-else-if="error" class="alert alert-danger text-center mx-3">
            {{ $t('examsManagement.error') }}
          </div>

          <template v-else>
            <!-- Mobile view -->
            <div class="d-block d-xl-none">
              <div v-if="classesToShow.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-calendar fa-2x mb-2"></i>
                <p>{{ $t('examsManagement.noClasses') }}</p>
              </div>

              <div v-for="classItem in classesToShow" :key="'mobile-' + classItem.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-0 text-sm">{{ classItem.name }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ $t('examsManagement.group') }}: {{ classItem.group_name || $t('examsManagement.mainGroup') }}
                      </p>
                    </div>
                    <div class="d-flex">
                      <router-link
                        :to="{ 
                          name: 'exams.schedule', 
                          params: { classId: classItem.id } 
                        }"
                        class="btn btn-link text-info p-0 mx-1"
                        :title="$t('examsManagement.viewSchedule')"
                        v-can="'exam_schedules.display'"
                      >
                        <i class="fas fa-calendar-alt fa-xs"></i>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop view -->
            <div class="table-responsive p-1 p-xl-4">
              <table class="table align-items-center mb-0 d-none d-xl-table">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('examsManagement.class') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('examsManagement.group') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('examsManagement.schedule') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="classesToShow.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted">
                      <i class="fas fa-calendar fa-2x mb-2"></i>
                      <p>{{ $t('examsManagement.noClasses') }}</p>
                    </td>
                  </tr>
                  
                  <tr v-for="classItem in classesToShow" :key="classItem.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ classItem.name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ isTeacher ? $t('examsManagement.myGroup') : (classItem.group_name || $t('examsManagement.mainGroup')) }}
                      </p>
                    </td>
                    <td class="align-middle">
                      <router-link
                        :to="{ 
                          name: 'exams.schedule', 
                          params: { 
                            classId: classItem.id, 
                            groupId: classItem.group_id || null
                          } 
                        }"
                        class="btn btn-link text-info p-1 mx-1"
                        :title="$t('examsManagement.viewSchedule')"
                        v-can="'exam_schedules.display'"
                      >
                        <i class="fas fa-calendar-alt fa-sm"></i>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import Cookies from 'js-cookie';

const store = useStore();
const isLoading = ref(false);
const error = ref(null);

const currentUser = computed(() => {
  return store.state.user || {
    role: {
      slug: Cookies.get('user_type') || 'teacher'
    }
  };
});

const isTeacher = computed(() => {
  return currentUser.value?.role?.slug === 'teacher';
});

const classesToShow = ref([]);

onMounted(async () => {
  try {
    isLoading.value = true;
    
    if (isTeacher.value) {
      await store.dispatch('teacher/fetchTeacherClasses', currentUser.value.id);
      classesToShow.value = store.state.teacher.teacherClasses || [];
    } else {
      await store.dispatch('classes/fetchClasses', {
        page: 1,
        per_page: 100, 
        search: ''
      });
      classesToShow.value = store.state.classes.classes || [];
    }
  } catch (err) {
    error.value = err.message || 'error';
    console.error('Error:', err);
  } finally {
    isLoading.value = false;
  }
});
</script>